import axios from 'axios';

const api = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.startsWith('/auth')) {
      window.location.href = '/api/auth/login';
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  status: () => api.get('/auth/status'),
  logout: () => api.post('/auth/logout')
};

export const clientsApi = {
  getAll: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  archive: (id) => api.post(`/clients/${id}/archive`),
  unarchive: (id) => api.post(`/clients/${id}/unarchive`),
  profile: (id) => api.get(`/clients/${id}/profile`),
  dashboardSummary: (id) => api.get(`/clients/${id}/dashboard-summary`)
};

export const pitchesApi = {
  getAll: () => api.get('/pitches'),
  getByClient: (clientId) => api.get(`/pitches/client/${clientId}`),
  create: (data) => api.post('/pitches', data),
  bulkCreate: (clientId, pitches) => api.post('/pitches/bulk', { clientId, pitches }),
  update: (id, data) => api.put(`/pitches/${id}`, data),
  delete: (id) => api.delete(`/pitches/${id}`),
  saidYes: (id, data) => api.post(`/pitches/${id}/said-yes`, data),
  flag: (id, weekKey) => api.post(`/pitches/${id}/flag`, { weekKey }),
  unflag: (id) => api.post(`/pitches/${id}/unflag`),
  scheduleInterview: (id, data) => api.post(`/pitches/${id}/schedule-interview`, data),
};

export const emailApi = {
  getSignature: (clientId) => api.get('/email/signature', { params: clientId ? { clientId } : {} }),
  getPitchText: (pitchId) => api.get(`/email/pitch-text/${pitchId}`),
  sendPitch: (data) => api.post('/email/pitch', data),
  sendFollowUp: (data) => api.post('/email/follow-up', data),
  previewOpportunity: (clientId, pitchIds) => api.post('/email/preview-opportunity', { clientId, pitchIds }),
  previewEOW: (clientId, pitchIds) => api.post('/email/preview-eow', { clientId, pitchIds }),
  createOpportunityDraft: (clientId, pitchIds, accountEmail) => api.post('/email/opportunity-draft', { clientId, pitchIds, ...(accountEmail ? { accountEmail } : {}) }),
  createEOWDraft: (clientId, pitchIds, accountEmail) => api.post('/email/eow-draft', { clientId, pitchIds, ...(accountEmail ? { accountEmail } : {}) }),
  weeklyStatus: () => api.get('/email/weekly-status')
};

export const googleApi = {
  getAuthUrl: () => api.get('/google/auth'),
  getStatus: () => api.get('/google/status'),
  getAccounts: () => api.get('/google/accounts'),
  disconnect: (email) => api.post('/google/disconnect', email ? { email } : {}),
  saveCredentials: (data) => api.post('/google/credentials', data),
  readSheet: (sheetId, range) => api.get(`/google/sheet/${sheetId}`, { params: range ? { range } : {} }),
  updateSheet: (sheetId, range, values) => api.post(`/google/sheet/${sheetId}/update`, { range, values }),
  appendSheet: (sheetId, range, values) => api.post(`/google/sheet/${sheetId}/append`, { range, values })
};

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data)
};

export const pitchingApi = {
  sync: (clientId) => api.post(`/pitching/sync/${clientId}`),
  preview: (clientId) => api.get(`/pitching/preview/${clientId}`),
  cancel: (jobId) => api.post(`/pitching/cancel/${jobId}`),
  reimportBooking: (clientId) => api.post(`/pitching/import-booking/${clientId}`),
  syncAll: () => api.post('/pitching/sync-all'),
  getTabs: (clientId) => api.get(`/pitching/tabs/${clientId}`),
  eligible: (clientId, sendType, tabName) => api.get(`/pitching/eligible/${clientId}/${sendType}`, { params: tabName ? { tabName } : {} }),
  noteBlocked: (clientId, sendType, tabName) => api.get(`/pitching/note-blocked/${clientId}/${sendType}`, { params: tabName ? { tabName } : {} })
};

export const formFillingApi = {
  queue: () => api.get('/form-filling/queue'),
  fill: (pitchId) => api.post('/form-filling/fill', { pitchId }),
  status: (jobId) => api.get(`/form-filling/status/${jobId}`),
  uploadProfile: (clientId, file) => {
    const form = new FormData();
    form.append('profile', file);
    return api.post(`/form-filling/profile/${clientId}`, form);
  },
  deleteProfile: (clientId) => api.delete(`/form-filling/profile/${clientId}`),
  markSubmitted: (pitchId) => api.post(`/form-filling/mark-submitted/${pitchId}`),
};

export const bookingReportApi = {
  preview: (clientId) => api.get(`/booking-report/client/${clientId}/preview`),
  syncPitch: (pitchId) => api.post(`/booking-report/sync/${pitchId}`),
  addOffered: (clientId, pitchId) => api.post('/booking-report/add-offered', { clientId, pitchId }),
  resync: (clientId) => api.post(`/booking-report/resync/${clientId}`),
  status: (clientId) => api.get(`/booking-report/status/${clientId}`)
};

export const masterListApi = {
  search: (search) => api.get('/master-list', { params: search ? { search } : {} }),
  // expectedName: the server refuses if that row no longer holds this podcast (list out of date)
  update: (rowNum, fields, expectedName) => api.patch(`/master-list/${rowNum}`, { ...fields, expectedName }),
  delete: (rowNum, expectedName) => api.delete(`/master-list/${rowNum}`, { params: { expectedName } }),
};

export default api;
